import { FC, JSX, useMemo } from 'react';
import _sortBy from 'lodash/sortBy';
import { ECommentRichContentCategory, IMentionUserRichContent, TComment } from 'shared/models';
import { Link } from '@remix-run/react';
import { useSemanticUrlBuilder } from '~/utils/use-semantic-url-builder';
import { useActiveToCurrentArticle } from '@article/shared/utils/use-active-to-current-article';
import useReflowClick from '~/utils/use-reflow-click';
import { ClickPosition } from '~/utils/click-position';
import { $CurrentArticleAsAvailable } from '@article/shared/atoms/current-article';
import { useAtomValue } from 'jotai';

import './style.scss';

interface ICommentContentProps {
  comment: TComment;
}

export const CommentContent: FC<ICommentContentProps> = (props) => {
  const { comment } = props;
  const { richContent, text } = comment;
  const article = useAtomValue($CurrentArticleAsAvailable);
  const buildSemanticUrl = useSemanticUrlBuilder();
  const activeToCurrentArticle = useActiveToCurrentArticle();

  const handleCommentRichContentClick = useReflowClick(
    {
      reflowBehavior: () => {
        activeToCurrentArticle(ClickPosition.comment_mentioned_user);
      },
      clickEvent: () => ({
        current_position: ClickPosition.comment_mentioned_user,
        page_click_id: comment.commentId,
      }),
    },
    [activeToCurrentArticle],
  );

  const richContentArray = useMemo<(string | JSX.Element)[]>(() => {
    if (!richContent?.length) {
      return [];
    }

    const sortedRichContent = _sortBy(richContent, '.start');

    let cursor = 0;
    const end = text.length;

    let richContentCursor = 0;
    const richContentEnd = sortedRichContent.length;

    const res: (string | JSX.Element)[] = [];

    while (cursor < end) {
      if (richContentCursor < richContentEnd) {
        res.push(text.substring(cursor, end));
        cursor = end;
        break;
      }

      const currentRich = sortedRichContent[richContentCursor];

      if (cursor === currentRich.start) {
        const { start, length } = currentRich;
        if (currentRich.type === ECommentRichContentCategory.MENTION_USER) {
          const rich = currentRich as IMentionUserRichContent;
          const { mentionUserId } = rich;

          const mentionedUerHref = buildSemanticUrl(`/user/${mentionUserId}/share`, article.articleRegion);

          res.push(
            <Link className="comment-mentioned-name" to={mentionedUerHref} onClick={handleCommentRichContentClick}>
              {text.substring(start, start + length)}
            </Link>,
          );
        } else {
          res.push(text.substring(start, start + length));
        }

        richContentCursor += 1;
        cursor += length;
      } else {
        res.push(text.substring(cursor, currentRich.start));
        cursor = currentRich.start;
      }
    }

    return res;
  }, [handleCommentRichContentClick]);

  if (!richContent?.length) {
    return <p className="comment-content">{text}</p>;
  }

  return <p className="comment-content">{richContentArray}</p>;
};
